import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowDown, Zap, Battery, Timer, Clock, ParkingCircle, Wrench, Leaf, Ruler, Briefcase, ShoppingBag, Building, Truck, ChevronDown, Send, Loader2, CircleDollarSign } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import BookingModal from "@/components/BookingModal";
import CookieConsent from "@/components/CookieConsent";
import api from "@/lib/api";

const HERO_IMG = "https://customer-assets.emergentagent.com/job_urban-mobility-35/artifacts/trpq1t5n_biro-big-estrima.jpg";
const SMALL_IMG = "https://customer-assets.emergentagent.com/job_urban-mobility-35/artifacts/2pb5q7d8_biro-classic-small-b2211_1%20%281%29.jpg";
const SMALL_SIDE = "https://customer-assets.emergentagent.com/job_urban-mobility-35/artifacts/6mhxvt8g_biro-classic-small-b2211_2%20%281%29.jpg";
const BIG_IMG = "https://customer-assets.emergentagent.com/job_urban-mobility-35/artifacts/9kuw3yw5_biro-classic-big-b2211_1.jpg";
const BIG_REAR = "https://customer-assets.emergentagent.com/job_urban-mobility-35/artifacts/6jyc9z1k_biro-classic-big-b2211_5.jpg";
const SMALL_REAR = "https://customer-assets.emergentagent.com/job_urban-mobility-35/artifacts/ei8pzebw_biro-classic-small-b2211_3%20%281%29.jpg";

function useFadeIn() {
    const ref = useRef(null);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const obs = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) { el.classList.add("visible"); obs.unobserve(el); } }, { threshold: 0.12 });
        obs.observe(el);
        return () => obs.disconnect();
    }, []);
    return ref;
}

function S({ children, className = "", id }) {
    const ref = useFadeIn();
    return <section id={id} ref={ref} className={`section-fade-in ${className}`}>{children}</section>;
}

const BATTERIES = [
    { id: "45maxi", label: "45 Maxi", speed: "45 km/h", range: "Upp till 100 km", charge: "Ca 5 timmar", desc: "Maximal räckvidd. Anslut till ett vanligt 220 V-uttag." },
    { id: "45remove", label: "45 ReMove", speed: "45 km/h", range: "Upp till 55 km", charge: "Ca 3 timmar", desc: "Uttagbart batteri som en trolley. Ladda hemma, på kontoret eller var du vill." },
    { id: "60maxi", label: "60 Maxi", speed: "60 km/h", range: "Upp till 90 km", charge: "Ca 5 timmar", desc: "Högre hastighet med lång räckvidd. Kräver B-körkort." },
];

const USE_CASES = [
    { icon: Building, title: "Stan", desc: "Smidiga resor i stadstrafik utan att leta parkering" },
    { icon: Clock, title: "Pendling", desc: "Effektiv pendling med minimala kostnader" },
    { icon: ShoppingBag, title: "Andrabil", desc: "Perfekt komplement till familjebilen för korta resor" },
    { icon: Briefcase, title: "Företag", desc: "Kostnadseffektivt tjänstefordon för korta ärenden" },
    { icon: Truck, title: "Leveranser", desc: "Birò Big rymmer över 300 liter – perfekt för last mile" },
];

const FAQ_ITEMS = [
    { q: "Behöver jag körkort?", a: "Birò Small (45 km/h) kräver AM-körkort (mopedklass). Birò Big (60 km/h) kräver B-körkort." },
    { q: "Hur laddar jag Birò?", a: "Via ett vanligt 220 V-uttag. Birò med Maxi-batteri laddas på ca 5 timmar, ReMove-batteriet på ca 3 timmar. ReMove-batteriet är uttagbart och kan laddas var som helst." },
    { q: "Var kan jag parkera?", a: "Biròs kompakta format (ca 1,8 m lång) gör det betydligt enklare att hitta parkeringsplatser jämfört med en konventionell bil. Kontrollera lokala parkeringsregler." },
    { q: "Vad kostar det att köra Birò?", a: "Energiförbrukningen är upp till 60 % lägre än en vanlig bil. Underhållskostnaderna är upp till 75 % lägre tack vare färre mekaniska komponenter." },
    { q: "Kan jag provköra innan jag bestämmer mig?", a: "Absolut! Boka en 24-timmars provkörning för 500 kr. Du hämtar i Nacka eller får leverans inom Stockholms tullar." },
    { q: "Är Birò undantagen från trängselskatt?", a: "Ja, Birò klassas som ett lättmotorfordon och är undantagen från trängselskatt i Stockholm." },
];

function ContactForm() {
    const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
    const [sending, setSending] = useState(false);
    const handleSubmit = async (e) => {
        e.preventDefault();
        setSending(true);
        try { await api.post("/contact-form", form); toast.success("Tack! Vi återkommer snart."); setForm({ name: "", email: "", phone: "", message: "" }); }
        catch { toast.error("Kunde inte skicka meddelandet."); } finally { setSending(false); }
    };
    return (
        <form onSubmit={handleSubmit} className="space-y-4 max-w-md" data-testid="contact-form">
            <Input data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Namn" required />
            <Input data-testid="contact-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="E-post" required />
            <Input data-testid="contact-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Telefon (valfritt)" />
            <Textarea data-testid="contact-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Ditt meddelande" required className="min-h-[100px]" />
            <Button data-testid="contact-submit-btn" type="submit" disabled={sending} className="bg-black text-white hover:bg-neutral-800 w-full md:w-auto px-8">
                {sending ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Send size={14} className="mr-2" />}
                {sending ? "Skickar..." : "Skicka meddelande"}
            </Button>
        </form>
    );
}

function FaqItem({ q, a }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="border-b border-neutral-200">
            <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between py-5 text-left" data-testid={`faq-${q.slice(0, 20)}`}>
                <span className="text-sm font-medium pr-8">{q}</span>
                <ChevronDown size={18} className={`text-neutral-400 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && <p className="text-sm text-neutral-500 leading-relaxed pb-5 pr-8">{a}</p>}
        </div>
    );
}

export default function HomePage() {
    const [bookingOpen, setBookingOpen] = useState(false);
    const [showStickyCta, setShowStickyCta] = useState(false);
    const [activeBattery, setActiveBattery] = useState("45maxi");

    useEffect(() => {
        const handleScroll = () => setShowStickyCta(window.scrollY > 600);
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const bat = BATTERIES.find((b) => b.id === activeBattery);

    return (
        <div className="bg-white min-h-screen" data-testid="home-page">
            <Navbar onBookingClick={() => setBookingOpen(true)} />

            {/* ═══ 1. HERO ═══ */}
            <section className="relative min-h-screen flex items-end pb-16 md:pb-24" data-testid="hero-section">
                <div className="absolute inset-0">
                    <img src={HERO_IMG} alt="Birò i stadsmiljö" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-transparent" />
                </div>
                <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full">
                    <h1 className="text-5xl sm:text-6xl lg:text-7xl font-light tracking-tighter leading-[0.95] text-white mb-5">
                        Staden blev precis<br />lite mindre.
                    </h1>
                    <p className="text-base md:text-lg text-white/80 max-w-lg mb-10 leading-relaxed">
                        100 % elektrisk. Extremt kompakt. Byggd för att göra vardagens resor enklare.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3">
                        <button data-testid="hero-booking-cta" onClick={() => setBookingOpen(true)}
                            className="inline-flex items-center justify-center gap-2 bg-white text-black px-8 py-4 text-xs uppercase tracking-[0.2em] font-medium hover:bg-neutral-100 transition-colors">
                            Boka provkörning <ArrowRight size={16} />
                        </button>
                        <a href="#models"
                            className="inline-flex items-center justify-center gap-2 border border-white/40 text-white px-8 py-4 text-xs uppercase tracking-[0.2em] font-medium hover:bg-white/10 transition-colors" data-testid="hero-explore-cta">
                            Utforska Birò <ArrowDown size={16} />
                        </a>
                    </div>
                </div>
            </section>

            {/* ═══ 2. SNABBA NYCKELTAL ═══ */}
            <S className="py-16 md:py-20 border-b border-neutral-100">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
                        {[
                            { value: "45–60", unit: "km/h", label: "Topphastighet" },
                            { value: "100", unit: "km", label: "Räckvidd" },
                            { value: "100%", unit: "", label: "Elektrisk" },
                            { value: "1,8m", unit: "", label: "Total längd" },
                        ].map((s, i) => (
                            <div key={i} className="text-left">
                                <div className="stat-number text-4xl md:text-5xl text-black leading-none">{s.value}<span className="text-lg text-neutral-400 ml-1">{s.unit}</span></div>
                                <p className="text-xs uppercase tracking-[0.15em] text-neutral-500 mt-2">{s.label}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </S>

            {/* ═══ 3. VIDEO / BIRÒ I VERKLIGHETEN ═══ */}
            <S className="py-20 md:py-28 bg-[#F5F5F5]">
                <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
                    <p className="text-xs uppercase tracking-[0.2em] text-neutral-500 mb-4">Birò i verkligheten</p>
                    <h2 className="text-3xl sm:text-4xl font-normal tracking-tight mb-10">Se hur Birò förändrar stadskörning</h2>
                    <div className="aspect-video bg-black/5 border border-neutral-200 flex items-center justify-center max-w-4xl mx-auto overflow-hidden">
                        <iframe
                            className="w-full h-full"
                            src="https://www.youtube.com/embed/T7D1T6wP3aY?rel=0&modestbranding=1"
                            title="Birò i stadsmiljö"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        />
                    </div>
                    <p className="text-xs text-neutral-400 mt-4">Birò – it's easy! Film från Estrima</p>
                </div>
            </S>

            {/* ═══ 4. VARFÖR BIRÒ? ═══ */}
            <S id="why" className="py-20 md:py-28">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <p className="text-xs uppercase tracking-[0.2em] text-neutral-500 mb-4">Varför Birò?</p>
                    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-normal tracking-tight leading-tight mb-16 max-w-2xl">En liten förändring som gör stor skillnad</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {[
                            { icon: ParkingCircle, title: "Mindre tid på parkering", stat: "Upp till 90 % mindre tid*", desc: "Biròs kompakta format gör det betydligt enklare att hitta en plats jämfört med en vanlig bil." },
                            { icon: Wrench, title: "Mindre underhåll", stat: "Upp till 75 % lägre kostnad*", desc: "Färre mekaniska komponenter och en enkel elektrisk drivlina innebär mindre traditionellt bilunderhåll." },
                            { icon: Leaf, title: "Lägre energiförbrukning", stat: "Upp till 60 % lägre*", desc: "Ett mycket mindre och lättare fordon behöver betydligt mindre energi för vardagens transporter." },
                            { icon: Ruler, title: "Extremt kompakt", stat: "1,8 m lång · 1,1 m bred", desc: "Birò är mer lik en scooter eller motorcykel i format än en traditionell bil. Utan trängselskatt." },
                        ].map((item, i) => (
                            <div key={i} className="border border-neutral-200 p-8 flex gap-5" data-testid={`usp-card-${i}`}>
                                <div className="flex-shrink-0 w-11 h-11 border border-neutral-200 flex items-center justify-center">
                                    <item.icon size={20} className="text-neutral-500" />
                                </div>
                                <div>
                                    <h3 className="text-base font-medium mb-1">{item.title}</h3>
                                    <p className="text-sm font-medium text-black mb-2">{item.stat}</p>
                                    <p className="text-sm text-neutral-500 leading-relaxed">{item.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                    <p className="text-[11px] text-neutral-400 mt-6">*Siffrorna kommer från Estrima/Birò och jämför Birò med en konventionell personbil.</p>
                </div>
            </S>

            {/* ═══ 5. VÄLJ DIN BIRÒ (Small vs Big) ═══ */}
            <S id="models" className="py-20 md:py-28 bg-[#F5F5F5]">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <p className="text-xs uppercase tracking-[0.2em] text-neutral-500 mb-4">Modeller</p>
                    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-normal tracking-tight leading-tight mb-16">Välj din Birò</h2>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* SMALL */}
                        <div className="bg-white border border-neutral-200 overflow-hidden" data-testid="model-card-small">
                            <Link to="/biro-small" className="block">
                                <div className="bg-white flex items-center justify-center p-8 hover:scale-[1.02] transition-transform" style={{height:'340px'}}>
                                    <img src={SMALL_IMG} alt="Birò Small" className="max-w-full max-h-full object-contain" />
                                </div>
                            </Link>
                            <div className="p-8 pt-4">
                                <h3 className="text-2xl font-medium tracking-tight mb-2">Birò Small</h3>
                                <p className="text-sm font-medium text-neutral-600 mb-3">Maximalt smidig. Minimalt format.</p>
                                <p className="text-sm text-neutral-500 leading-relaxed mb-4">Byggd för dig som prioriterar smidighet i stan. Trots det kompakta formatet finns ett internt förvaringsutrymme på 122 liter.</p>
                                <div className="flex items-center gap-6 text-xs text-neutral-500 mb-6">
                                    <span className="flex items-center gap-1.5"><Zap size={14} /> 45 km/h</span>
                                    <span className="flex items-center gap-1.5"><Battery size={14} /> Upp till 100 km</span>
                                    <span className="flex items-center gap-1.5"><Ruler size={14} /> 122 L</span>
                                </div>
                                <Link to="/biro-small">
                                    <Button className="bg-black text-white hover:bg-neutral-800 w-full py-5 text-xs uppercase tracking-[0.15em]" data-testid="explore-small-btn">
                                        Utforska modellen <ArrowRight size={14} className="ml-2" />
                                    </Button>
                                </Link>
                            </div>
                        </div>
                        {/* BIG */}
                        <div className="bg-white border border-neutral-200 overflow-hidden" data-testid="model-card-big">
                            <Link to="/biro-big" className="block">
                                <div className="bg-white flex items-center justify-center p-8 hover:scale-[1.02] transition-transform" style={{height:'340px'}}>
                                    <img src={BIG_IMG} alt="Birò Big" className="max-w-full max-h-full object-contain" />
                                </div>
                            </Link>
                            <div className="p-8 pt-4">
                                <h3 className="text-2xl font-medium tracking-tight mb-2">Birò Big</h3>
                                <p className="text-sm font-medium text-neutral-600 mb-3">Samma smidighet. Betydligt mer lastutrymme.</p>
                                <p className="text-sm text-neutral-500 leading-relaxed mb-4">Modellen för dig som behöver mer plats för jobb, shopping, utrustning eller vardagens transporter. Över 300 liter lastutrymme.</p>
                                <div className="flex items-center gap-6 text-xs text-neutral-500 mb-6">
                                    <span className="flex items-center gap-1.5"><Zap size={14} /> 60 km/h</span>
                                    <span className="flex items-center gap-1.5"><Battery size={14} /> Upp till 90 km</span>
                                    <span className="flex items-center gap-1.5"><Ruler size={14} /> 300+ L</span>
                                </div>
                                <Link to="/biro-big">
                                    <Button className="bg-black text-white hover:bg-neutral-800 w-full py-5 text-xs uppercase tracking-[0.15em]" data-testid="explore-big-btn">
                                        Utforska modellen <ArrowRight size={14} className="ml-2" />
                                    </Button>
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </S>

            {/* ═══ 6. VÄLJ BATTERI ═══ */}
            <S id="battery" className="py-20 md:py-28">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <p className="text-xs uppercase tracking-[0.2em] text-neutral-500 mb-4">Batteri & prestanda</p>
                    <h2 className="text-3xl sm:text-4xl font-normal tracking-tight mb-12">Tre batterialternativ</h2>
                    <div className="flex gap-2 mb-10">
                        {BATTERIES.map((b) => (
                            <button key={b.id} onClick={() => setActiveBattery(b.id)} data-testid={`battery-tab-${b.id}`}
                                className={`px-5 py-2.5 text-xs uppercase tracking-[0.15em] font-medium border transition-colors ${activeBattery === b.id ? "bg-black text-white border-black" : "bg-white text-neutral-600 border-neutral-200 hover:border-neutral-400"}`}>
                                {b.label}
                            </button>
                        ))}
                    </div>
                    {bat && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8" data-testid="battery-info">
                            <div>
                                <div className="stat-number text-5xl md:text-6xl text-black">{bat.speed.split(" ")[0]}</div>
                                <p className="text-xs uppercase tracking-[0.15em] text-neutral-500 mt-2">Topphastighet</p>
                            </div>
                            <div>
                                <div className="stat-number text-5xl md:text-6xl text-black">{bat.range.replace("Upp till ", "")}</div>
                                <p className="text-xs uppercase tracking-[0.15em] text-neutral-500 mt-2">Räckvidd</p>
                            </div>
                            <div>
                                <div className="stat-number text-5xl md:text-6xl text-black">{bat.charge.replace("Ca ", "")}</div>
                                <p className="text-xs uppercase tracking-[0.15em] text-neutral-500 mt-2">Laddtid</p>
                            </div>
                        </div>
                    )}
                    {bat && <p className="text-sm text-neutral-500 leading-relaxed max-w-xl">{bat.desc}</p>}
                </div>
            </S>

            {/* ═══ 7. ANVÄNDNINGSOMRÅDEN ═══ */}
            <S className="py-20 md:py-28 bg-black text-white">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <p className="text-xs uppercase tracking-[0.2em] text-neutral-500 mb-4">Användning</p>
                    <h2 className="text-3xl sm:text-4xl font-normal tracking-tight mb-16">Birò passar dig som...</h2>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
                        {USE_CASES.map((uc, i) => (
                            <div key={i} className="text-center" data-testid={`use-case-${i}`}>
                                <div className="w-14 h-14 mx-auto border border-neutral-700 flex items-center justify-center mb-4">
                                    <uc.icon size={22} className="text-neutral-400" />
                                </div>
                                <h3 className="text-sm font-medium mb-1">{uc.title}</h3>
                                <p className="text-xs text-neutral-400 leading-relaxed">{uc.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </S>

            {/* ═══ 8. PROVKÖRNING ═══ */}
            <S id="test-drive" className="py-24 md:py-32">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                        <div>
                            <p className="text-xs uppercase tracking-[0.2em] text-neutral-500 mb-4">Provkörning</p>
                            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-normal tracking-tight leading-tight mb-6">Du förstår Birò<br />när du kör den.</h2>
                            <p className="text-sm text-neutral-500 leading-relaxed mb-8 max-w-md">
                                Det är svårt att beskriva hur liten, smidig och lättkörd Birò faktiskt är. Boka en provkörning och upplev själv hur enkelt det är att ta sig genom stan, hitta parkering och göra vardagens kortare resor enklare.
                            </p>
                            <div className="space-y-3 mb-8">
                                {["Välj datum och tid", "Betala 500 kr", "Vi bekräftar inom 24 timmar", "Kör i 24 timmar"].map((step, i) => (
                                    <div key={i} className="flex items-center gap-3">
                                        <span className="w-7 h-7 border border-neutral-300 flex items-center justify-center text-xs font-medium">{i + 1}</span>
                                        <span className="text-sm text-neutral-600">{step}</span>
                                    </div>
                                ))}
                            </div>
                            <button data-testid="test-drive-booking-cta" onClick={() => setBookingOpen(true)}
                                className="inline-flex items-center gap-2 bg-black text-white px-8 py-4 text-xs uppercase tracking-[0.2em] font-medium hover:bg-neutral-800 transition-colors">
                                Boka din provkörning <ArrowRight size={16} />
                            </button>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <img src={SMALL_SIDE} alt="Birò Small sida" className="w-full aspect-square object-contain bg-white" />
                            <img src={BIG_REAR} alt="Birò Big baksida" className="w-full aspect-square object-contain bg-white" />
                        </div>
                    </div>
                </div>
            </S>

            {/* ═══ 9. FAQ ═══ */}
            <S id="faq" className="py-20 md:py-28 bg-[#F5F5F5]">
                <div className="max-w-3xl mx-auto px-6 md:px-12">
                    <p className="text-xs uppercase tracking-[0.2em] text-neutral-500 mb-4">Vanliga frågor</p>
                    <h2 className="text-3xl sm:text-4xl font-normal tracking-tight mb-10">Frågor & svar</h2>
                    <div className="bg-white border border-neutral-200">
                        {FAQ_ITEMS.map((faq, i) => <FaqItem key={i} q={faq.q} a={faq.a} />)}
                    </div>
                </div>
            </S>

            {/* ═══ 10. KONTAKT ═══ */}
            <S id="contact" className="py-20 md:py-28">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
                        <div>
                            <p className="text-xs uppercase tracking-[0.2em] text-neutral-500 mb-4">Kontakt</p>
                            <h2 className="text-3xl sm:text-4xl font-normal tracking-tight mb-6">Har du frågor?</h2>
                            <p className="text-sm text-neutral-500 leading-relaxed mb-8">Kontakta oss så återkommer vi så snart som möjligt.</p>
                            <div className="space-y-3 text-sm text-neutral-500">
                                <p className="font-medium text-black">Biro Stockholm AB</p>
                                <p>Manhemsvägen 7, 131 46 Nacka</p>
                                <p><a href="mailto:info@biro.se" className="text-black underline">info@biro.se</a></p>
                            </div>
                            <div className="space-y-3 text-sm text-neutral-500 mt-6 pt-6 border-t border-neutral-100">
                                <p className="font-medium text-black">Staffan Lidström</p>
                                <p>Linnégatan 89, 216 15 Limhamn</p>
                                <p><a href="tel:+46706687242" className="text-black underline">+46 706 68 72 42</a></p>
                                <p><a href="mailto:staffan@biro.se" className="text-black underline">staffan@biro.se</a></p>
                            </div>
                        </div>
                        <ContactForm />
                    </div>
                </div>
            </S>

            {/* ═══ 11. SLUT-CTA ═══ */}
            <S className="py-24 md:py-32 bg-black text-white">
                <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
                    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-normal tracking-tight leading-tight mb-6 text-white">Redo att tänka lite mindre?</h2>
                    <p className="text-base text-neutral-400 max-w-md mx-auto mb-10 leading-relaxed">Boka en provkörning och upplev Birò – det smartaste sättet att ta sig genom staden.</p>
                    <button data-testid="final-booking-cta" onClick={() => setBookingOpen(true)}
                        className="inline-flex items-center gap-2 bg-white text-black px-10 py-4 text-xs uppercase tracking-[0.2em] font-medium hover:bg-neutral-100 transition-colors">
                        Boka provkörning <ArrowRight size={16} />
                    </button>
                </div>
            </S>

            <Footer />

            {/* Sticky CTA – mobil */}
            <div className={`fixed bottom-0 left-0 right-0 z-40 p-3 bg-white/95 backdrop-blur-sm border-t border-neutral-200 md:hidden transition-all duration-300 ${showStickyCta ? "translate-y-0" : "translate-y-full"}`} data-testid="sticky-cta-mobile">
                <button onClick={() => setBookingOpen(true)} className="w-full bg-black text-white py-3.5 text-xs uppercase tracking-[0.2em] font-medium flex items-center justify-center gap-2">
                    Boka provkörning <ArrowRight size={14} />
                </button>
            </div>

            {/* Sticky CTA – desktop */}
            <div className={`sticky-cta hidden md:block ${showStickyCta ? "" : "hidden"}`} data-testid="sticky-cta">
                <button onClick={() => setBookingOpen(true)} className="bg-black text-white px-8 py-3.5 text-xs uppercase tracking-[0.2em] font-medium shadow-2xl hover:bg-neutral-800 transition-colors flex items-center gap-2">
                    Boka provkörning <ArrowRight size={14} />
                </button>
            </div>

            <BookingModal open={bookingOpen} onOpenChange={setBookingOpen} />
            <CookieConsent />
        </div>
    );
}
